import React from 'react';
import { FormTitle } from '../form/form-title.component';
import CloseActiveItemButton from '../common/close-active-item-button';
import { isEmpty, startCase, toLower } from 'lodash';
import { useTranslation } from 'react-i18next';

export interface RecordAnalysisProps {
  title?: string;
  onClose?: (id: string) => void;
  analyzeRecordJobId?: string;

  //analysis results
  inferred_context: string;
  data_quality_notes: string;
  patterns_and_insights: string[];
  analysis?: Record<string, unknown>;
}

function RecordAnalysis(props: RecordAnalysisProps) {
  const { title, onClose, inferred_context, data_quality_notes, patterns_and_insights, analysis, analyzeRecordJobId } =
    props;
  const { t } = useTranslation();

  const renderAnalysisRecord = React.useCallback((obj: Record<string, unknown>): React.JSX.Element => {
    const render = (value: unknown): React.JSX.Element | string => {
      if (Array.isArray(value)) {
        return (
          <ul className="ml-6 list-disc">
            {value.map((item, idx) => (
              <li key={idx}>{render(item)}</li>
            ))}
          </ul>
        );
      } else if (typeof value === 'object' && value !== null) {
        return (
          <ul className="list-none space-y-1">
            {Object.entries(value).map(([key, val]) => {
              if (!key.trim()) return null;
              return (
                <li key={key} className="flex flex-col">
                  <span className="text-gray-800 font-semibold">{toTitleCase(key)}:</span>
                  <div className="ml-4">{render(val)}</div>
                </li>
              );
            })}
          </ul>
        );
      } else {
        return String(value);
      }
    };

    return <div className="text-gray-700 text-sm">{render(obj)}</div>;
  }, []);

  return (
    <div className="z-20 flex flex-shrink-0 flex-col">
      <div className="flex">
        {title && <FormTitle title={`${t('analyse-records')} - ${title}`} />}
        {onClose && analyzeRecordJobId && (
          <div className="flex min-w-min justify-end">
            <CloseActiveItemButton onClick={() => onClose(analyzeRecordJobId)} />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 p-4">
        {patterns_and_insights && patterns_and_insights.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="text-lg font-semibold">{t('patterns-and-insights', 'Patterns and Insights')}:</div>
            <ol className="list-decimal pl-4">
              {patterns_and_insights.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ol>
          </div>
        )}

        {data_quality_notes && (
          <div className="flex flex-col gap-2">
            <div className="text-lg font-semibold">{t('data-quality-notes', 'Data Quality Notes')}:</div>
            <div>{data_quality_notes}</div>
          </div>
        )}

        {inferred_context && (
          <div className="flex flex-col gap-2">
            <div className="text-lg font-semibold">{t('inferred-context', 'Inferred Context')}:</div>
            <div>{inferred_context}</div>
          </div>
        )}

        {!isEmpty(analysis) && (
          <div className="flex flex-col gap-2">
            <div className="text-lg font-semibold">{t('analysis', 'Analysis')}:</div>
            <div>{renderAnalysisRecord(analysis)}</div>
          </div>
        )}
      </div>
    </div>
  );
}
const toTitleCase = (key: string) => startCase(toLower(key));

export default RecordAnalysis;
